<template>
  <div class="content">

    <div v-if="step < 4" class="ds-toolbar actions-down">
      <!-- WSTECZ trzymamy przy lewej krawędzi paska (a DALEJ przy prawej) —
           tak samo jak na pasku wyników i w pozostałych kreatorach. -->
      <button class="ds-btn ds-btn--secondary btn-prev" v-if="step > 1" @click="previousStep()">
        <span class="ds-btn__arrow" aria-hidden="true">&#8592;</span>
        {{ t('back2').toUpperCase() }}
      </button>

      <label class="ds-switch" for="archival-location-view-toggle" v-if="step === 1">
        <span class="ds-switch__label">{{ t('map').toUpperCase() }}</span>
        <input id="archival-location-view-toggle" type="checkbox" v-model="useMapLocationList" />
        <span class="ds-switch__track"></span>
      </label>

      <div class="ds-toolbar__spacer"></div>

      <!-- Wskaźnik postępu kreatora filtrów archiwum (3 kroki) -->
      <div class="ds-stepper">
        <span class="ds-stepper__label">
          {{ t('step-indicator', { current: step, total: 3 }) }}
        </span>
        <span class="ds-stepper__dots" aria-hidden="true">
          <span
            class="ds-stepper__dot"
            :class="{ 'is-active': step === n }"
            v-for="n in 3"
            :key="n"
          ></span>
        </span>
      </div>

      <div class="ds-toolbar__spacer"></div>

      <button
          class="ds-btn ds-btn--primary btn-next"
          v-if="step < 4"
          @click="nextStep()"
          :disabled="!canGoNext"
      >
        {{ step < 3 ? t('next').toUpperCase() : t('apply').toUpperCase() }}
        <span class="ds-btn__arrow" aria-hidden="true">&#8594;</span>
      </button>
    </div>

    <div
      v-show="!hideFilters"
      class="archival-filters"
      :id="`archival-filters`"
    >
      <!-- STEP 1 -->
      <div v-if="step === 1" class="step-content step1">
        <component :is="locationListComponent" :store="filtersStore" cssClass="archival" />
        <date-range :store="filtersStore" />
      </div>

      <!-- STEP 2 -->
      <div v-else-if="step === 2 && !isSG" class="step-content step2">
        <violation-list :store="filtersStore" />
        <vehicle-categories :store="filtersStore" :step="step" />
        <lane-numbers :store="filtersStore" :step="step" />
        <archival-vehicle-data />
      </div>

      <div v-else-if="step === 2 && isSG" class="step-content step2 step2--sg">
        <archival-vehicle-data />
      </div>

      <!-- STEP 3 -->
      <div v-else-if="step === 3" class="step-content step3 sort-panel">
        <div class="sort-panel__row" v-for="n in (isSG ? 2 : 3)" :key="n">
          <select-filter
              class="sort-panel__control"
              :name="t('type')"
              :fieldName="`orderByColumnName${n}`"
              type="column"
              :store="filtersStore"
              :step="step"
              :allow-empty="n > 1"
          />
          <select-filter
              class="sort-panel__control"
              :name="t('direction')"
              :fieldName="`orderDirection${n}`"
              type="direction"
              :store="filtersStore"
              :step="step"
          />
        </div>
      </div>
    </div>

    <!-- STEP 4 -->
    <div v-if="step === 4" class="content">
      <!-- Pasek akcji wyników: powrót do filtrów, licznik, eksporty -->
      <div class="ds-toolbar results-toolbar">
        <!-- Przycisk powrotu do filtrów -->
        <button class="ds-btn ds-btn--secondary btn-prev-top" @click="previousStep()">
          <span class="ds-btn__arrow" aria-hidden="true">&#8592;</span>
          {{ t('back2').toUpperCase() }}
        </button>

        <div class="total-count">
          <!-- Liczba wyników -->
          <span>{{ t('number-of-results') }}: <strong>{{ totalCount }}</strong></span>
        </div>

        <div class="ds-toolbar__spacer"></div>

        <!-- Eksport PDF -->
        <div class="dropdown-wrapper" @click="toggleDropdown('pdf')">
<!--          <button class="btn" :disabled="noData">-->
          <button class="btn">
            <span>PDF</span>
          </button>
          <ul v-show="isOpenPdf" class="dropdown-list">
            <li @click.stop="selectOption('pdf')">
              <span v-show="isDownloadingPdf" class="spinner-border spinner-border-sm me-1"></span>
              PDF
            </li>
            <li @click.stop="selectOption('pdf-with-image')">
              <span v-show="isDownloadingPdfWithImage" class="spinner-border spinner-border-sm me-1"></span>
              PDF {{ t('with-image') }}
            </li>
          </ul>
        </div>

        <!-- Eksport CSV -->
        <div class="dropdown-wrapper" @click="toggleDropdown('csv')">
<!--          <button class="btn" :disabled="noData">-->
          <button class="btn">
            <span>CSV</span>
          </button>
          <ul v-show="isOpenCsv" class="dropdown-list">
            <li @click.stop="selectOption('csv')">
              <span v-show="isDownloadingCsv" class="spinner-border spinner-border-sm me-1"></span>
              CSV
            </li>
            <li @click.stop="selectOption('csv-with-image')">
              <span v-show="isDownloadingCsvWithImage" class="spinner-border spinner-border-sm me-1"></span>
              CSV {{ t('with-image') }}
            </li>
          </ul>
        </div>

        <!-- Eksport XLSX -->
        <div class="dropdown-wrapper" @click="toggleDropdown('xlsx')">
<!--          <button class="btn" :disabled="noData">-->
          <button class="btn">
            <span>XLSX</span>
          </button>
          <ul v-show="isOpenXlsx" class="dropdown-list">
            <li @click.stop="selectOption('xlsx')">
              <span v-show="isDownloadingXlsx" class="spinner-border spinner-border-sm me-1"></span>
              XLSX
            </li>
            <li @click.stop="selectOption('xlsx-with-image')">
              <span v-show="isDownloadingXlsxWithImage" class="spinner-border spinner-border-sm me-1"></span>
              XLSX {{ t('with-image') }}
            </li>
          </ul>
        </div>
      </div>
      <!-- Loader -->
      <spinner-element :loading="loading" />
      <!-- Komunikat gdy brak danych -->
      <div v-if="noData" class="loading d-flex justify-content-center m-3">
        {{ t('no-data-for-selected-parameters') }}
      </div>
      <!-- Lista wyników -->
      <vehicle-list
        :data-service="vehService"
        :vehicle-list="filtersStore.savedVehicles && filtersStore.savedVehicles.length > 0 ? filtersStore.savedVehicles : vehicles"
        :timer-visible="false"
        details-path="vehicleArch"
        :is-from-tags="isFromTags"
        :show-page-size-selector="true"
        :page-size="pageSize"
        :page-size-options="pageSizeOptions"
        @openDetails="goToDetailsView"
        @page-size-change="setPageSize"
      />
      <!-- Paginacja -->
      <PagePanel
        :page-num="pageNumber"
        :page-size="pageSize"
        :total-size="totalCount"
        v-on:setPage="setPage"
      />
    </div>
  </div>
</template>

<script>
// Importujemy wszystkie zależności, filtry i komponenty
import { useArchivalFiltersStore } from "@/stores/archivalFilters";
import ArchivalMeasurementsService from "../services/ArchivalMeasurementsService";
import LocationList from "./filters/LocationList.vue";
import LocationList2 from "./filters/LocationList2.vue";
import DateRange from "./filters/DateRange.vue";
import LaneNumbers from "./filters/LaneNumbers.vue";
import VehicleCategories from "./filters/VehicleCategories.vue";
import ViolationList from "./filters/ViolationList.vue";
import ArchivalVehicleData from "./archival/ArchivalVehicleData.vue";
import SelectFilter from "./filters/SelectFilter.vue";
import VehicleList from "@/components/VehicleList";
import moment from "moment";
import SpinnerElement from "./SpinnerElement.vue";
import PagePanel from "@/components/table/PagePanel";
import { ref, watch, computed } from 'vue';
import { isSG } from "@/config/appVersion";
import { useRoute, useRouter } from "vue-router";

moment.locale("pl");

export default {
  // Krok procesu przekazywany jako props
  props: {
    step: { type: Number, required: true }
  },
  components: {
    LocationList,
    LocationList2,
    DateRange,
    LaneNumbers,
    VehicleCategories,
    ViolationList,
    ArchivalVehicleData,
    SelectFilter,
    VehicleList,
    SpinnerElement,
    PagePanel,
  },
  name: "ArchivalMeasurements",
  emits: ["nextStep", "previousStep", "clear"],

  // Setup API (composition API)
  setup(props, { emit }) {
    const filtersStore = useArchivalFiltersStore();
    // const step = computed(() => props.step);

    // Stan komponentu
    const vehicles = ref([]);
    const loading = ref(false);
    const noData = ref(false);
    const hideFilters = ref(false);
    const route = useRoute();
    const router = useRouter();

    const vehService = ArchivalMeasurementsService;

    // Stany spinnerów eksportu
    const isDownloadingPdf = ref(false);
    const isDownloadingPdfWithImage = ref(false);
    const isDownloadingCsv = ref(false);
    const isDownloadingCsvWithImage = ref(false);
    const isDownloadingXlsx = ref(false);
    const isDownloadingXlsxWithImage = ref(false);

    // Paginacja
    const pageSizeOptions = [10, 25, 50];
    const getValidPageNumber = (value) => {
      const page = Number(value || 1);
      return Number.isInteger(page) && page > 0 ? page : 1;
    };
    const getValidPageSize = (value) => {
      const size = Number(value || 10);
      return pageSizeOptions.includes(size) ? size : 10;
    };
    const pageNumber = ref(getValidPageNumber(route.query.page));
    const pageSize = ref(getValidPageSize(route.query.limit));
    const totalCount = ref(0);

    // Otwarcie dropdownów eksportu
    const isOpenPdf = ref(false);
    const isOpenCsv = ref(false);
    const isOpenXlsx = ref(false);

    // Czy wyniki są z tagów?
    const isFromTags = ref(false);
    const useMapLocationList = ref(false);
    const locationListComponent = computed(() =>
        useMapLocationList.value ? "LocationList2" : "LocationList"
    );

    const hasAppliedFilters = computed(
      () => Object.keys(filtersStore.appliedFilters || {}).length > 0
    );
    const getFiltersForResults = () =>
      props.step === 4 && hasAppliedFilters.value
        ? filtersStore.appliedFilters
        : filtersStore.filters;

    // reaguj na zmianę query (np. powrót z details)
    watch(
        () => [route.query.page, route.query.limit],
        ([p, limit]) => {
          const newPage = getValidPageNumber(p);
          const newPageSize = getValidPageSize(limit);
          const hasChanged =
              newPage !== pageNumber.value ||
              newPageSize !== pageSize.value;

          if (hasChanged) {
            pageNumber.value = newPage;
            pageSize.value = newPageSize;
            filtersStore.clearSavedResults?.();

            if (props.step === 4) {
              refreshVehicles();
            }
          }
        }
    );

    const refreshVehicles = async () => {
      loading.value = true;
      noData.value = false;

      // console.log('refreshVehicles');

      try {
        let data;
        const filtersForRequest = getFiltersForResults();

        if (!filtersForRequest.taggedOnly) {
          data = await vehService.getAll(pageNumber.value, pageSize.value, filtersForRequest);
          isFromTags.value = false;
        } else {
          data = await vehService.getAllWithTags(filtersForRequest);
          isFromTags.value = true;
        }

        // --- Ujednolicenie odpowiedzi ---
        // 1) TAGI: czasem API zwraca tablicę
        if (Array.isArray(data)) {
          totalCount.value = data.length;
          const totalPages =
              pageSize.value > 0 ? Math.ceil(totalCount.value / pageSize.value) : 0;

          if (totalPages > 0 && pageNumber.value > totalPages) {
            pageNumber.value = totalPages;
            router.replace({
              path: route.path,
              query: {
                ...route.query,
                page: String(pageNumber.value),
                limit: String(pageSize.value),
              },
            });
          }

          const start = (pageNumber.value - 1) * pageSize.value;
          vehicles.value = data.slice(start, start + pageSize.value);
          noData.value = data.length === 0;
          return;
        }

        const items = data?.content ?? data?.items ?? [];
        const total =
            data?.totalCount ??
            data?.totalElements ??   // bardzo częste w paginacji
            data?.total ??           // fallback
            (Array.isArray(items) ? items.length : 0);

        totalCount.value = Number(total) || 0;
        vehicles.value = Array.isArray(items) ? items : [];

        // totalPages (jeśli backend nie zwraca)
        const totalPages =
            data?.totalPages ??
            (pageSize.value > 0 ? Math.ceil(totalCount.value / pageSize.value) : 0);

        // --- Jeśli jesteś poza zakresem stron, cofnij i pobierz ponownie ---
        if (totalPages > 0 && pageNumber.value > totalPages) {
          pageNumber.value = totalPages;

          // sync do URL
          router.replace({
            path: route.path,
            query: {
              ...route.query,
              page: String(pageNumber.value),
              limit: String(pageSize.value),
            },
          });

          // ponów pobranie już dla poprawnej strony
          const data2 = await vehService.getAll(pageNumber.value, pageSize.value, filtersForRequest);

          const items2 = data2?.content ?? data2?.items ?? [];
          const total2 =
              data2?.totalCount ??
              data2?.totalElements ??
              data2?.total ??
              (Array.isArray(items2) ? items2.length : 0);

          totalCount.value = Number(total2) || 0;
          vehicles.value = Array.isArray(items2) ? items2 : [];
        }

        noData.value = totalCount.value === 0;
      } catch (e) {
        filtersStore.setErrors(e);
        vehicles.value = [];
        totalCount.value = 0;
        noData.value = true;
      } finally {
        loading.value = false;
      }
    };

    const setPageSize = (newPageSize) => {
      const size = getValidPageSize(newPageSize);
      if (size === pageSize.value) return;

      pageSize.value = size;
      pageNumber.value = 1;
      hideFilters.value = true;
      filtersStore.clearSavedResults?.();

      router.replace({
        path: route.path,
        query: {
          ...route.query,
          page: "1",
          limit: String(size),
        },
      });

      refreshVehicles();
    };

    const canGoNext = computed(() => {
      if (props.step !== 1) return true;
      const locs = filtersStore.filters?.locations;
      return Array.isArray(locs) && locs.length > 0;
    });

    watch(
        () => props.step,
        (newStep) => {
          // wyjście z wyników → czyść URL
          if (newStep !== 4 && (route.query.page || route.query.limit)) {
            router.replace({ path: route.path, query: {} });
          }

          if (newStep !== 4) {
            hideFilters.value = false;
          }

          // wejście w wyniki
          if (newStep === 4) {
            if (!hasAppliedFilters.value) {
              filtersStore.setAppliedFilters({ ...filtersStore.filters });
            }

            // Zapamiętane wyniki (powrót z widoku szczegółów) używamy tylko wtedy, gdy mamy też
            // zapamiętaną łączną liczbę pomiarów — inaczej paginacja pokazałaby 0 stron.
            const savedTotalCount = Number(filtersStore.savedTotalCount) || 0;

            if (filtersStore.savedVehicles?.length > 0 && savedTotalCount > 0) {
              vehicles.value = [...filtersStore.savedVehicles];
              totalCount.value = savedTotalCount;
              // Cache jest czyszczony przy każdej zmianie filtrów, więc zapamiętane wyniki zawsze
              // pochodzą z tych filtrów — stąd można z nich odtworzyć tryb "tylko oznaczone".
              isFromTags.value = !!getFiltersForResults().taggedOnly;
              noData.value = vehicles.value.length === 0;
              loading.value = false;
            } else {
              // Bez kompletnego cache pobieramy dane od nowa — czyścimy zapamiętaną listę, żeby
              // widok nie pokazywał starych wyników (template ma pierwszeństwo dla savedVehicles).
              filtersStore.clearSavedResults?.();
              refreshVehicles();
            }
          }
        },
        { immediate: true }
    );

    // Odśwież na wejście w widok wyników
    // watch(() => filtersStore.isResult, (val) => {
    //   if (val) {
    //     refreshVehicles();
    //   }
    // });

    watch(
        () => filtersStore.filters,
        () => {
          hideFilters.value = false;

          // Na kroku wyników zmiana filtrów z panelu bocznego (ikona kosza, odznaczenie
          // lokalizacji) musi od razu przeładować listę. `removeItem`/`addItem` czyszczą
          // zapamiętane wyniki i `appliedFilters`, więc bez pobrania danych widok zostawał
          // pusty — z wynikami dla poprzednich filtrów albo bez żadnych.
          if (props.step === 4) {
            if (!filtersStore.hasCoreFilters()) {
              // Brak lokalizacji lub zakresu dat — nie ma czego pobierać.
              vehicles.value = [];
              totalCount.value = 0;
              noData.value = true;
              loading.value = false;
              return;
            }

            filtersStore.setAppliedFilters({ ...filtersStore.filters });
            filtersStore.clearSavedResults?.();

            // Nowy zestaw filtrów = nowy wynik, więc wracamy na pierwszą stronę.
            if (pageNumber.value !== 1) {
              pageNumber.value = 1;
              router.replace({
                path: route.path,
                query: {
                  ...route.query,
                  page: "1",
                  limit: String(pageSize.value),
                },
              });
            }

            refreshVehicles();
            return;
          }

          vehicles.value = [];
          totalCount.value = 0;
          noData.value = false;
        },
        { deep: true }
    );

    const nextStep = () => {
      if (!canGoNext.value) return;
      emit('nextStep');
    };

    const previousStep = () => {
      emit('previousStep');
    };

    // Wyczyszczenie widoku i reset filtrów
    const clear = () => {
      emit('clear');
      hideFilters.value = false;
    };

    // Zwracamy wszystko co potrzebne w template
    return {
      filtersStore,
      vehicles,
      loading,
      noData,
      hideFilters,
      nextStep,
      previousStep,
      clear,
      refreshVehicles,
      vehService,
      isDownloadingPdf,
      isDownloadingPdfWithImage,
      isDownloadingCsv,
      isDownloadingCsvWithImage,
      isDownloadingXlsx,
      isDownloadingXlsxWithImage,
      pageNumber,
      pageSize,
      pageSizeOptions,
      setPageSize,
      totalCount,
      isOpenPdf,
      isOpenCsv,
      isOpenXlsx,
      isFromTags,
      useMapLocationList,
      locationListComponent,
      isSG,
      canGoNext,
      route,
      router,
      // step,
    };
  },

  // Reszta funkcji (options API)
  methods: {
    // Odśwież dane na podstawie aktualnych filtrów
    refreshData: function () {
      this.hideFilters = true;
      this.refreshVehicles();
    },
    // Otwieranie dropdownów eksportu
    toggleDropdown(type) {
      // if (this.noData) return;
      this.isOpenPdf = type === 'pdf' ? !this.isOpenPdf : false;
      this.isOpenCsv = type === 'csv' ? !this.isOpenCsv : false;
      this.isOpenXlsx = type === 'xlsx' ? !this.isOpenXlsx : false;
    },
    // Obsługa kliknięcia eksportu
    selectOption(type) {
      if (type == 'pdf') {
        this.isDownloadingPdf = true;
      }
      if (type == 'pdf-with-image') {
        this.isDownloadingPdfWithImage = true;
      }
      if (type == 'csv') {
        this.isDownloadingCsv = true;
      }
      if (type == 'csv-with-image') {
        this.isDownloadingCsvWithImage = true;
      }
      if (type == 'xlsx') {
        this.isDownloadingXlsx = true;
      }
      if (type == 'xlsx-with-image') {
        this.isDownloadingXlsxWithImage = true;
      }
      // Pobierz plik w odpowiednim formacie
      if (!this.isFromTags) {
        this.retrieveZip(type);
      } else {
        this.retrieveTagsZip(type);
      }
    },
    // Główna funkcja pobierania archiwalnych pojazdów (nieużywana)
    retrieveArchivalMeasurements() {
      this.loading = true;
      this.refreshData();
    },
    // Ustaw spinner (ładowanie) na podstawie wyników
    setLoading: function () {
      this.loading = this.vehicles.length === 0;
    },
    // Reakcja na zmianę lokalizacji
    runChangedLocations(locations) {
      if (!this.isResult) return;
      if (!locations || locations.length === 0) {
        this.filtersStore.checkRequireData();
        this.clear();
      } else {
        this.refreshVehicles();
      }
    },
    // Reakcja na zmianę filtrów
    runChangedFilters() {
      if (!this.isResult) return;
      this.filtersStore.checkRequireData();
      this.hideFilters = false;
      this.vehicles = [];
    },
    // Zmiana strony w paginacji
    setPage(event) {
      this.pageNumber = event;
      this.filtersStore.clearSavedResults?.();

      // zapis do URL (żeby powrót działał)
      this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          page: String(event),
          limit: String(this.pageSize),
        },
      });

      this.refreshData();
    },
    // Obsługa spinnerów eksportu
    downloadingIcon(type, value) {
      if (type === "pdf") {
        this.isDownloadingPdf = value;
      }
      if (type === "pdf-with-image") {
        this.isDownloadingPdfWithImage = value;
      }
      if (type === "csv") {
        this.isDownloadingCsv = value;
      }
      if (type === "csv-with-image") {
        this.isDownloadingCsvWithImage = value;
      }
      if (type === "xlsx") {
        this.isDownloadingXlsx = value;
      }
      if (type === "xlsx-with-image") {
        this.isDownloadingXlsxWithImage = value;
      }
    },
    // Pobieranie ZIP z API (standard)
    retrieveZip(type) {
      this.downloadingIcon(type, true);
      this.vehService
        .getZip(type)
        .then((data) => {
          // console.log('archivalmeasurements.vue: data', data);
          this.vehService
            .fetch(data.link)
            .then((response) => {
              const link = document.createElement("a");
              link.href = URL.createObjectURL(response);
              // Nazwa pliku na podstawie daty
              const now = new Date();
              const year = now.getFullYear();
              const month = String(now.getMonth() + 1).padStart(2, '0');
              const day = String(now.getDate()).padStart(2, '0');
              const hours = String(now.getHours()).padStart(2, '0');
              const minutes = String(now.getMinutes()).padStart(2, '0');
              const seconds = String(now.getSeconds()).padStart(2, '0');
              const fileName = `archive_list_${day}_${month}_${year}_${hours}_${minutes}_${seconds}`;
              link.download = fileName;
              link.click();
              this.downloadingIcon(type, false);
              URL.revokeObjectURL(link.href);
            })
            .catch(console.error)
            .finally(() => {
              this.downloadingIcon(type, false);
              this.closeDropdown(type);
            });
        })
        .catch((error) => {
          this.loading = false;
          this.filtersStore.setErrors(error);
          this.closeDropdown(type);
        });
    },
    // Pobieranie ZIP z API (dla wyników z tagami)
    retrieveTagsZip(type) {
      this.downloadingIcon(type, true);
      this.vehService
        .getTagsZip(type)
        .then((data) => {
          this.vehService
            .fetch(data.link)
            .then((response) => {
              const link = document.createElement("a");
              link.href = URL.createObjectURL(response);
              const now = new Date();
              const year = now.getFullYear();
              const month = String(now.getMonth() + 1).padStart(2, '0');
              const day = String(now.getDate()).padStart(2, '0');
              const hours = String(now.getHours()).padStart(2, '0');
              const minutes = String(now.getMinutes()).padStart(2, '0');
              const seconds = String(now.getSeconds()).padStart(2, '0');
              const fileName = `archive_list_${day}_${month}_${year}_${hours}_${minutes}_${seconds}`;
              link.download = fileName;
              link.click();
              URL.revokeObjectURL(link.href);
            })
            .catch(console.error)
            .finally(() => {
              this.downloadingIcon(type, false);
              this.closeDropdown(type);
            });
        })
        .catch((error) => {
          this.loading = false;
          this.filtersStore.setErrors(error);
          this.closeDropdown(type);
        });
    },
    // Otwieranie/ukrywanie filtrów po kliknięciu
    clickFilters(event) {
      if (event.target.className !== "collapsed") {
        this.hideFilters = false;
      }
    },
    // Przejście do widoku szczegółowego pojazdu (do routera)
    goToDetailsView(vehicle) {
      this.filtersStore.setSavedState(
        this.filtersStore.filters.locations,
        this.filtersStore.filters,
        this.vehicles,
        this.totalCount
      );
      const path = `/vehicleArch/${vehicle.id}/${vehicle.location.id}/${this.getCreatedAt(vehicle)}`;
      const query = {
        page: String(this.pageNumber),
        limit: String(this.pageSize),
        backTo: this.$route.fullPath,
      };
      this.$router.push({ path, query });
    },
    // Pobierz datę utworzenia pojazdu w ISO
    getCreatedAt(vehicle) {
      return moment(vehicle.createdAt).toISOString(false);
    },
    // Zamknięcie dropdowna eksportu po pobraniu
    closeDropdown(type) {
      if (type === 'pdf' || type === 'pdf-with-image') {
        this.isOpenPdf = false;
      } else if (type === 'csv' || type === 'csv-with-image') {
        this.isOpenCsv = false;
      } else if (type === 'xlsx' || type === 'xlsx-with-image') {
        this.isOpenXlsx = false;
      }
    },
  },
};
</script>

<style scoped>
/* =============================================================================
   Archiwum pomiarów — kreator filtrów (kroki 1-3) + widok wyników (krok 4).
   Wygląd paneli, kontrolek i przycisków pochodzi z design-system.css; tutaj
   zostaje wyłącznie układ tego widoku.
   ========================================================================== */

.content,
.step-content {
  width: 100%;
  min-height: 478px;
}

/* --- paski akcji ---------------------------------------------------------- */

.actions-down,
.results-toolbar {
  background: var(--surface-primary);
}

.results-toolbar {
  flex-wrap: wrap;
}

.total-count {
  font-size: var(--font-size-md);
  color: var(--text-secondary);
}

/* --- kroki kreatora: siatki paneli --------------------------------------- */

.step-content {
  display: grid;
  gap: var(--spacing-md);
  align-items: start;
  padding: var(--spacing-md);
  background: var(--surface-secondary);
}

/* Krok 1: lokalizacje + zakres dat */
.step1 {
  grid-template-columns: minmax(380px, 1.6fr) minmax(280px, 1fr);
}
/* Krok 2: przekroczenia, kategorie, pasy w pierwszym rzędzie,
   dane pojazdu na całą szerokość pod nimi. */
.step2 {
  grid-template-columns:
    minmax(280px, 1fr)
    minmax(300px, 1.15fr)
    minmax(140px, 0.4fr);
  grid-template-areas:
    "violations categories lanes"
    "vehicle vehicle vehicle";
}

.step2 :deep(.violations) {
  grid-area: violations;
}

.step2 :deep(.vehicle-categories) {
  grid-area: categories;
}

.step2 :deep(.lane-numbers) {
  grid-area: lanes;
}

.step2 :deep(.vehicle-data) {
  grid-area: vehicle;
}

/* Wariant SG: w kroku 2 zostają tylko dane pojazdu. */
.step2--sg {
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: "vehicle";
}

/* --- krok 3: poziomy sortowania ------------------------------------------ */

.step3 {
  grid-template-columns: minmax(0, 1fr);
}

.sort-panel {
  display: grid;
  gap: var(--spacing-lg);
}

/* Każdy poziom sortowania jako panel z dwoma polami wyboru. */
.sort-panel__row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-lg);
  padding: var(--spacing-lg);
  background: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  box-shadow: var(--shadow-panel);
}

:deep(.sort-panel__control) {
  font-size: var(--font-size-sm);
}

/* --- eksport danych (PDF / CSV / XLSX) ----------------------------------- */

.dropdown-wrapper {
  position: relative;
  display: inline-block;
}

.dropdown-wrapper .btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 92px;
  min-height: var(--control-height);
  padding: 0 var(--spacing-lg);
  background: var(--surface-primary);
  border: 1px solid var(--border-strong);
  border-radius: var(--control-radius);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  cursor: pointer;
}

.dropdown-wrapper .btn:hover {
  background: var(--surface-hover);
}

.dropdown-list {
  position: absolute;
  top: calc(100% + 2px);
  right: 0;
  z-index: 20;
  min-width: 180px;
  margin: 0;
  padding: var(--spacing-xs) 0;
  list-style: none;
  background: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  box-shadow: var(--shadow-overlay);
  text-align: left;
}

.dropdown-list li {
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  cursor: pointer;
  white-space: nowrap;
}

.dropdown-list li:hover {
  background: var(--surface-hover);
}
</style>
